import { useEffect } from 'react'
import { useFormikContext } from 'formik'

import { getInhabitantPropertyInfo } from '../../../../api'
import { FORMIK_EMPTY_VALUE } from '../../../../constants/validation'
import {
  isApiForbiddenStatus,
  isApiNotFoundStatus,
} from '../../../../utils/api'
import { isStaffUser } from '../../../../utils/user'
import {
  toBuildingAddressSelectItem,
  toInhabitantBuildingSelectItem,
} from '../../../Building'
import { toFlatSelectItem } from '../../../Flat'
import {
  toUserSelectItem,
  toUserWithPropertyInfoSelectItem,
} from '../../../User'
import { REQUEST_WIZARD_AI_WIZARD_USER_AUTOFILLED_KEY } from '../../Request.constants'

import { shouldFetchInhabitantPropertyInfo } from './useAutoCompleteAddressFormFields.utils'

export function useAutoCompleteAddressFormFields({
  buildingFieldName,
  contractFieldName,
  currentUser,
  flatFieldName,
  setIsSingleFlat,
  userFieldName,
}) {
  const { setFieldValue, setValues, values } = useFormikContext()
  const selectedUser = values[userFieldName]

  const isDwellerUserAcc = currentUser && !isStaffUser(currentUser)

  function applyAddress(user) {
    if (user?.propertyInfo) {
      const {
        propertyInfo: {
          building,
          buildings_count: buildingsCount,
          flat,
          flats_count: flatsCount,
        },
      } = user

      // If user has flats
      if (flatsCount > 0) {
        if (flatsCount === 1 && buildingsCount === 1) {
          setFieldValue(
            buildingFieldName,
            toInhabitantBuildingSelectItem(building)
          )
          setFieldValue(flatFieldName, toFlatSelectItem(flat))
          setIsSingleFlat?.(true)

          return
        }

        if (flatsCount > 1 && buildingsCount <= 1) {
          setFieldValue(
            buildingFieldName,
            toInhabitantBuildingSelectItem(building)
          )
          setFieldValue(flatFieldName, FORMIK_EMPTY_VALUE)
          setIsSingleFlat?.(false)

          return
        }
      }

      // The user is assigned to several buildings, so the address can't be
      // resolved automatically. Keep the building/flat that are already
      // selected (e.g. the ones used to pre-filter this applicant) instead of
      // resetting them.
      if (buildingsCount > 1) {
        setIsSingleFlat?.(false)

        return
      }

      // Clear for non-staff without flats
      if (!isStaffUser(user)) {
        setFieldValue(flatFieldName, FORMIK_EMPTY_VALUE)
        setFieldValue(buildingFieldName, FORMIK_EMPTY_VALUE)
        setIsSingleFlat?.(false)
      }
    }
  }

  useEffect(() => {
    // The AI Wizard already resolved building/flat for this user server-side,
    // so the cascade must not overwrite them.
    const isAIAutofilledUser =
      !!selectedUser?.[REQUEST_WIZARD_AI_WIZARD_USER_AUTOFILLED_KEY]

    if (!isAIAutofilledUser) {
      if (isDwellerUserAcc && !selectedUser) {
        const preparedCurrentUser = toUserSelectItem(currentUser)

        setFieldValue(userFieldName, preparedCurrentUser)
      }

      if (
        shouldFetchInhabitantPropertyInfo({
          contractValue: values[contractFieldName],
          selectedUser,
        })
      ) {
        getInhabitantPropertyInfo(selectedUser.value)
          .then(response =>
            applyAddress(toUserWithPropertyInfoSelectItem(response.data))
          )
          .catch(error => {
            // The applicant may be pre-filled from an external source (e.g. an
            // email) without an access check. When the inhabitant is not
            // accessible the profile request fails (404/403), so clear the
            // pre-filled applicant instead of keeping its name/avatar around.
            const status = error?.response?.status

            if (isApiNotFoundStatus(status) || isApiForbiddenStatus(status)) {
              setFieldValue(userFieldName, FORMIK_EMPTY_VALUE)
              setIsSingleFlat?.(false)

              return
            }

            console.error(error)
          })
      } else if (selectedUser && selectedUser.propertyInfo) {
        applyAddress(selectedUser)
      } else {
        setIsSingleFlat?.(false)
      }
    }
  }, [
    applyAddress,
    currentUser,
    isDwellerUserAcc,
    selectedUser,
    setFieldValue,
    setIsSingleFlat,
    userFieldName,
  ])

  useEffect(() => {
    if (values[contractFieldName]) {
      setValues(prev => ({
        ...prev,
        [buildingFieldName]: toBuildingAddressSelectItem(
          values[contractFieldName]?.building
        ),
        [flatFieldName]: toFlatSelectItem(values[contractFieldName]?.flat),
        [userFieldName]: toUserSelectItem(values[contractFieldName]?.user),
      }))
    }
  }, [values[contractFieldName]])
}
